import axios, { AxiosError, AxiosInstance } from 'axios';

/**
 * Account-suspension handling.
 *
 * The backend answers `402 Payment Required` on every route outside a small
 * allowlist once an account is suspended (see
 * `website-backend/src/controllers/middleware/subscription_status.rs`). Nothing
 * in the app interpreted that, so a suspended user got a banner at the top of a
 * page whose every query had failed — an ambiguous mess rather than a reason to
 * pay.
 *
 * A 402 now flips this store, and the app is replaced by a lockout screen whose
 * only exits are paying and signing out. Unlike the 401 case in
 * `./sessionExpiry`, the failed request is *rejected* rather than parked:
 * suspension is not something the user resolves in a dialog and resumes from,
 * and leaving requests pending would hold data behind a screen that exists
 * precisely to withhold it.
 */

type Listener = () => void;

/**
 * `useSyncExternalStore` compares snapshots by identity, so the snapshot object
 * is replaced only when something actually changes — returning a fresh object
 * per read would re-render forever.
 */
export interface SuspensionState {
  suspended: boolean;
  /** The backend's explanation, e.g. non-payment vs. an admin action. */
  reason: string | null;
}

const NOT_SUSPENDED: SuspensionState = { suspended: false, reason: null };

let state: SuspensionState = NOT_SUSPENDED;
const listeners = new Set<Listener>();

function emit() {
  listeners.forEach((listener) => { listener(); });
}

/** Subscribe to suspension-state changes. Returns an unsubscribe function. */
export function subscribeToAccountSuspension(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Current suspension state — the snapshot half of a `useSyncExternalStore` pair. */
export function accountSuspensionState(): SuspensionState {
  return state;
}

/**
 * Flag the account as suspended. Idempotent for a given reason, so the stream
 * of 402s a suspended session produces settles after the first one.
 */
export function markAccountSuspended(reason: string | null): void {
  if (state.suspended && state.reason === reason) return;
  state = { suspended: true, reason };
  emit();
}

/**
 * Clear the lockout — the account is paid up again. Driven by polling the
 * suspension summary rather than by the payment forms, so any route back to
 * good standing releases the app: a card added here, an admin lifting a
 * suspension, or a Stripe webhook landing after a retry elsewhere.
 */
export function clearAccountSuspension(): void {
  if (!state.suspended) return;
  state = NOT_SUSPENDED;
  emit();
}

/** Test-only reset so state doesn't leak between cases. */
export function resetAccountSuspensionForTests(): void {
  state = NOT_SUSPENDED;
  listeners.clear();
}

interface ApiErrorBody {
  error?: string;
  description?: string;
}

/**
 * Install the 402 response interceptor. Returns an eject function.
 *
 * Separate from the 401 interceptor rather than folded into it: the two
 * statuses want opposite treatment (park and replay vs. reject and lock out),
 * and keeping them apart means neither has to reason about the other.
 */
export function installAccountSuspensionInterceptor(client: AxiosInstance = axios): () => void {
  const id = client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 402) {
        const body = error.response.data as ApiErrorBody | undefined;
        markAccountSuspended(body?.description ?? null);
      }
      return Promise.reject(error);
    },
  );

  return () => { client.interceptors.response.eject(id); };
}
